import { useAnimationFrame } from 'framer-motion';
import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useAngular } from 'src/hooks/use-angular';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';

/**
 * 0--------------9
 * |              |
 * |    3----4    |
 * |    |    |    |
 * |    6----5    |
 * |              |
 * 1----2,7-------8
 */

function createPolygon(rect: {
  left: number;
  top: number;
  right: number;
  bottom: number;
}) {
  const polygon =
    `0% 0%,` + // 0
    `0% 100%,` + // 1
    `${rect.left}px 100%,` + // 2
    `${rect.left}px ${rect.top}px,` + // 3
    `${rect.right}px ${rect.top}px,` + // 4
    `${rect.right}px ${rect.bottom}px,` + // 5
    `${rect.left}px ${rect.bottom}px,` + // 6
    `${rect.left}px 100%,` + // 7
    `100% 100%,` + // 8
    `100% 0%`; // 9
  return polygon;
}

// the header and footer in our React app need to be dimmed, if Angular opens a modal
export const AngularModalDimmer: FC<{
  clipAngular?: boolean;
  children: ReactNode;
}> = ({ children, clipAngular }) => {
  const angular = useAngular();
  const isNewDesignActive = useIsNewDesignActive();

  const dimmerRef = useRef<HTMLDivElement>(null);

  const updateClipPath = useCallback(() => {
    if (!clipAngular) return;
    if (!angular.loaded) return;
    if (!angular.iframe) return;
    if (!dimmerRef.current) return;
    const polygon = createPolygon({
      left: angular.iframe.offsetLeft,
      top: angular.iframe.offsetTop - dimmerRef.current.offsetTop,
      right: angular.iframe.offsetLeft + angular.iframe.clientWidth,
      bottom:
        angular.iframe.offsetTop +
        angular.iframe.clientHeight -
        dimmerRef.current.offsetTop,
    });
    dimmerRef.current.style.clipPath = `polygon(${polygon})`;
    if (isNewDesignActive) dimmerRef.current.style.zIndex = '3';
  }, [angular.iframe, angular.loaded, clipAngular, isNewDesignActive]);

  useAnimationFrame(updateClipPath);

  useEffect(() => {
    window.addEventListener('resize', updateClipPath);
    return () => window.removeEventListener('resize', updateClipPath);
  }, [updateClipPath]);

  return (
    <div className="grid">
      {/* angular will add `.angular-modal` and `.angular-delay` classes to the body */}
      <div
        className={`
          pointer-events-none z-1 opacity-0 transition-opacity duration-150
          ease-linear [grid-area:1/1/1/1]
          accepted-exception:bg-[#000]
          [body.angular-modal_&]:pointer-events-auto
          [body.angular-modal_&]:opacity-50
        `}
        ref={dimmerRef}
      />
      <div className="[grid-area:1/1/1/1]">{children}</div>
    </div>
  );
};
